// Значения ответов анкеты: идентификатор, подпись и всё, что из ответа
// вычисляется.
//
// ЗАЧЕМ ЭТОТ ФАЙЛ. Раньше движок разбирал ответы по ДОСЛОВНОМУ ТЕКСТУ
// варианта: `budget_range === "до 10 тысяч долларов"`, `level.startsWith(
// "начальный")`, `currency === "манаты"`. Текст варианта — это копирайтинг:
// его перепишут при первой вычитке, поправят опечатку, поставят неразрывный
// пробел. Сопоставление тогда молча перестаёт срабатывать, движок не падает и
// не предупреждает — он считает бюджет неизвестным, и семья видит достижимыми
// страны, которые ей не по карману. Это худший вид ошибки в продукте: она
// выглядит как нормальный ответ.
//
// Поэтому в ответах хранится ИДЕНТИФИКАТОР, а числа и коды лежат здесь же,
// рядом с ним. Переименование подписи после этого не влияет на расчёт вообще.
//
// ПРАВИЛО, ПО КОТОРОМУ ВАРИАНТ ПОПАДАЕТ СЮДА: если значение ответа
// ИНТЕРПРЕТИРУЕТСЯ — сравнивается, отображается в код, участвует в фильтре, —
// у него обязан быть id. Если ответ только показывается человеку, подпись
// может остаться сама себе идентификатором.
//
// ЧЕГО ЗДЕСЬ НЕТ И ПОЧЕМУ. Был слой чтения ответов по «замороженным» старым
// подписям — на случай данных, сохранённых до перехода на id. Он убран:
// замер прода показал ноль анкет, ноль отчётов и ноль брифов, то есть слой
// защищал пустое множество. Платой за него был вечный налог — каждый новый
// вариант ответа обязан был получить выдуманное «старое» название, которого
// никто никогда не вводил, — и ложь следующему читателю, будто legacy-данные
// существуют.
//
// ЭТО ВЕРНО РОВНО ДО ПЕРВОЙ СОХРАНЁННОЙ АНКЕТЫ. Как только в edu_profiles
// появится хотя бы одна строка, идентификаторы ниже становятся частью
// контракта данных: переименование id — ломающее изменение, и чинится оно
// миграцией данных, а не подписями. См. ARCHITECTURE.md § Release.

export type AnswerOption = {
  id: string;
  label: string;
  /**
   * Вариант ОТРИЦАЕТ остальные: вместе с ними он не может быть верен.
   *
   * «Нет ограничений по питанию» + «халяль» — противоречие: одно из двух
   * ложно, и семья его не заметила. А «покажите варианты» + Турция — НЕ
   * противоречие, а мягкое предпочтение: сильного выбора нет, но склоняюсь.
   * Объявить взаимоисключающими оба значило бы выкинуть настоящую информацию
   * ради единообразия, поэтому флаг ставится по смыслу подписи, а не по форме
   * вопроса.
   *
   * ОДНО ОБЪЯВЛЕНИЕ, ДВА ПОТРЕБИТЕЛЯ: форма гасит по нему остальные варианты,
   * движок по нему же разрешает противоречие в уже сохранённых данных.
   * Правило, написанное в переключателе и отдельно в движке, — это второе
   * место, где логика разойдётся.
   */
  exclusive?: boolean;
};

// ── бюджет ────────────────────────────────────────────────────────────
// ceilingUsd — ВЕРХНЯЯ граница вилки в долларах. null означает «не знаю»:
// это не ноль и не бесконечность, а отсутствие ответа, и фильтр по деньгам
// на нём не применяется.
export type BudgetOption = AnswerOption & { ceilingUsd: number | null };

export const BUDGET_RANGES: readonly BudgetOption[] = [
  { id: "under_10k", label: "до 10 тысяч долларов", ceilingUsd: 10_000 },
  { id: "10_20k", label: "10–20 тысяч", ceilingUsd: 20_000 },
  { id: "20_35k", label: "20–35 тысяч", ceilingUsd: 35_000 },
  { id: "35_60k", label: "35–60 тысяч", ceilingUsd: 60_000 },
  { id: "over_60k", label: "больше 60 тысяч", ceilingUsd: Number.POSITIVE_INFINITY },
  { id: "unknown", label: "не знаю", ceilingUsd: null },
] as const;

// ── валюта семьи ──────────────────────────────────────────────────────
export type CurrencyOption = AnswerOption & { code: string };

export const BUDGET_CURRENCIES: readonly CurrencyOption[] = [
  { id: "azn", label: "манаты", code: "AZN" },
  { id: "usd", label: "доллары", code: "USD" },
  { id: "eur", label: "евро", code: "EUR" },
  // «Не думали» — тоже ответ: считаем в долларах, потому что вилки бюджета
  // долларовые, но это допущение, а не выбор семьи.
  { id: "not_considered", label: "не думали об этом", code: "USD" },
] as const;

// ── форматы обучения ──────────────────────────────────────────────────
export type FormatOption = AnswerOption & { format: string | null };

export const STUDY_FORMATS: readonly FormatOption[] = [
  { id: "bachelor", label: "бакалавриат", format: "bachelor" },
  { id: "foundation", label: "подготовительный год перед вузом", format: "foundation" },
  { id: "summer_school", label: "летняя школа", format: "summer_school" },
  { id: "boarding", label: "школа-пансион", format: "boarding" },
  { id: "language_course", label: "языковой курс", format: "language_course" },
  // null = «рассматриваем все форматы», а не «никаких».
  // «Пока не знаю» вместе с бакалавриатом — не противоречие, а склонность:
  // семья назвала предпочтение, не имея твёрдого выбора. exclusive НЕ ставим.
  { id: "unsure", label: "пока не знаю", format: null },
] as const;

// ── уровень языка ─────────────────────────────────────────────────────
export type LevelOption = AnswerOption & { cefr: string };

export const LANGUAGE_LEVELS: readonly LevelOption[] = [
  { id: "beginner", label: "начальный", cefr: "A2_or_below" },
  { id: "intermediate", label: "средний, понимаю но говорю с трудом", cefr: "B1" },
  { id: "good", label: "хороший, свободно общаюсь", cefr: "B2" },
  { id: "advanced", label: "очень хороший, читаю и пишу без словаря", cefr: "C1" },
] as const;

// ── год поступления ───────────────────────────────────────────────────
// cycle — то, чем цикл зовётся в справочнике (edu_cycle_dates.cycle). null =
// цикл не определён, и календарь к варианту не привязывается.
export type IntakeOption = AnswerOption & { cycle: string | null };

export const INTAKE_YEARS: readonly IntakeOption[] = [
  { id: "2027", label: "2027", cycle: "2027" },
  { id: "2028", label: "2028", cycle: "2028" },
  { id: "2029", label: "2029", cycle: "2029" },
  { id: "later", label: "позже", cycle: null },
  { id: "undecided", label: "ещё не решил(а)", cycle: null },
] as const;

// ── год вуза дома ─────────────────────────────────────────────────────
// hasYear — тот самый булев ответ, который переводит вердикт из «теряем год»
// в «поступаем сейчас». Раньше он вычислялся из startsWith по подписи.
export type PriorStudyOption = AnswerOption & { hasYear: boolean };

export const PRIOR_STUDY: readonly PriorStudyOption[] = [
  { id: "no", label: "нет", hasYear: false },
  {
    id: "in_progress_will_finish",
    label: "учусь сейчас, первый курс закончу до поступления",
    hasYear: true,
  },
  { id: "finished_year", label: "да, закончил(а) минимум год", hasYear: true },
  { id: "dropped_out", label: "да, но бросил(а) до конца года", hasYear: false },
] as const;

// ── языковой сертификат ───────────────────────────────────────────────
// hasCertificate отличает «нет» от любого названия экзамена.
//
// language — НА КАКОМ ЯЗЫКЕ этот сертификат, ISO 639-1. Без него движок
// закрывал требование «турецкий C1» английским IELTS: сертификат есть,
// значит требование выполнено. null = язык сертификата неизвестен («другой»),
// и это не то же самое, что совпадение.
export type CertificateOption = AnswerOption & {
  hasCertificate: boolean;
  language: string | null;
  /**
   * Код экзамена в справочнике edu_exams, когда он не совпадает с id варианта.
   * Без поля код = id. Id варианта при этом НЕ меняется: он лежит в уже
   * сохранённых ответах, и смена id потребовала бы переписать их.
   */
  examCode?: string;
};

/** Код справочника edu_exams, с которым сверяется сертификат семьи. */
export const certificateExamCode = (c: CertificateOption): string => c.examCode ?? c.id;

export const LANGUAGE_CERTIFICATES: readonly CertificateOption[] = [
  { id: "none", label: "нет", hasCertificate: false, language: null },
  { id: "ielts", label: "IELTS", hasCertificate: true, language: "en" },
  // TOEFL анкеты — это TOEFL iBT справочника (решение владельца 11.10, К3).
  // Другого TOEFL в справочнике нет, а вузы принимают именно iBT; без
  // examCode семья с TOEFL 90 против порога iBT 84 читала «ваш экзамен не
  // принимают».
  { id: "toefl", label: "TOEFL", hasCertificate: true, language: "en", examCode: "toefl_ibt" },
  { id: "duolingo", label: "Duolingo", hasCertificate: true, language: "en" },
  { id: "testdaf", label: "TestDaF", hasCertificate: true, language: "de" },
  // HSK — китайский. Вузы Китая называют УРОВЕНЬ (HSK 4, HSK 5), иногда балл
  // внутри него, и анкета спрашивает именно так: уровень вариантом, балл —
  // отдельным необязательным полем (см. HSK_LEVELS и q_hsk_level).
  { id: "hsk", label: "HSK", hasCertificate: true, language: "zh" },
  { id: "other", label: "другой", hasCertificate: true, language: null },
] as const;

// ── HSK: уровень, а не балл ───────────────────────────────────────────
// Код в справочнике edu_exams — тот же, что id сертификата.
//
// ПОРОГ HSK — ЭТО УРОВЕНЬ. «HSK 4 от 210» и «HSK 5 от 180» — разные уровни, и
// балл одного не сравним с баллом другого: 210 на четвёртом уровне не «больше»
// 180 на пятом. Поэтому языковой балл семьи (language_score) для HSK — номер
// уровня 1–6, и порог требования (min_value) — тоже номер уровня. Балл внутри
// уровня едет компонентом total и сверяется, только когда уровни совпали.
//
// Отдельных кодов на уровень (hsk4, hsk5) НЕТ НАМЕРЕННО: это один экзамен, и
// шесть строк справочника разошлись бы в сроках и стоимости при первой правке.
export const HSK_CODE = "hsk";

export const HSK_LEVELS: readonly AnswerOption[] = [
  { id: "1", label: "1" },
  { id: "2", label: "2" },
  { id: "3", label: "3" },
  { id: "4", label: "4" },
  { id: "5", label: "5" },
  { id: "6", label: "6" },
] as const;

/**
 * Уровень HSK из строки: целое от 1 до 6, иначе null.
 *
 * Только голое число. «HSK 5 · 180» в пороге — проза, а не уровень, и читать
 * из неё первую цифру значило бы угадывать: она там же, где балл.
 */
export function hskLevelOf(raw: unknown): number | null {
  if (typeof raw !== "string" && typeof raw !== "number") return null;
  const t = String(raw).trim();
  return /^[1-6]$/.test(t) ? Number(t) : null;
}

/**
 * Балл HSK внутри уровня строкой: целое от 0 до 300, иначе null.
 *
 * Мусор и число за шкалой — «балла нет», а не ноль: ноль сравнился бы с
 * порогом и дал уверенное «не хватает».
 */
export function hskScoreOf(raw: unknown): string | null {
  if (typeof raw !== "string" && typeof raw !== "number") return null;
  const t = String(raw).trim();
  if (!/^\d{1,3}$/.test(t)) return null;
  const n = Number(t);
  return n <= 300 ? String(n) : null;
}

// ── язык обучения ─────────────────────────────────────────────────────
// Ответ на «на каком языке готов(а) учиться». Значение ИНТЕРПРЕТИРУЕТСЯ —
// им отсекаются языковые требования чужих программ, — значит у него обязан
// быть идентификатор.
//
// code = null у двух вариантов, и это не язык, а режим: «любой, выучу» и
// «только на том, который уже знаю» не сужают выбор программ. Второе
// формально сужает, но чем именно — анкета не спрашивает, и додумывать за
// семью хуже, чем не сужать.
export type StudyLanguageOption = AnswerOption & { code: string | null };

export const STUDY_LANGUAGES: readonly StudyLanguageOption[] = [
  { id: "en", label: "английский", code: "en" },
  { id: "de", label: "немецкий", code: "de" },
  { id: "tr", label: "турецкий", code: "tr" },
  // Русский — для России: обучение почти везде на русском, нужен ТРКИ B1 или
  // экзамен вуза. Отбирает языковые строки тем же правилом, что и остальные.
  { id: "ru", label: "русский", code: "ru" },
  // «Любой, выучу» — та же мягкость, что «покажите варианты».
  { id: "any", label: "любой, выучу", code: null },
  // «Только на том, который уже знаю» — вообще НЕ отрицание: это сужение, и
  // вместе с названным языком оно совместимо (названный язык может быть тем
  // самым знакомым). exclusive здесь был бы прямой ошибкой.
  { id: "known_only", label: "только на том, который уже знаю", code: null },
] as const;

/**
 * Язык, на котором семья получит отчёт.
 *
 * ЭТО НЕ q_language_pref. Тот про язык ОБУЧЕНИЯ: ребёнок, собравшийся учиться
 * по-английски в Турции, читает отчёт вместе с родителями — и читают они его
 * не обязательно по-английски. Две разные вещи, и подставить одну вместо
 * другой значит выдать родителям английский документ на основании выбора
 * ребёнка.
 *
 * Подписи стоят НА СВОИХ ЯЗЫКАХ: тот, кому нужен азербайджанский, узнаёт
 * «azərbaycan dili» вернее, чем слово «азербайджанский».
 */
export type DocLanguageOption = { id: string; label: string; code: "ru" | "az" | "en" };

/**
 * АНГЛИЙСКОГО ЗДЕСЬ НЕТ, И ЭТО НЕ ЗАБЫТО. Английский отчёт существует и
 * считается — он нужен для проверки, — но семье не предлагается: справочник
 * по-английски догоняет, и выбравший его получил бы английские заголовки с
 * русскими ценами, датами и пометками. Половинчатый документ хуже
 * одноязычного, и это то же решение, что и по самому вопросу про язык.
 *
 * Появится английский справочник — вернуть сюда одну строку.
 */
export const DOC_LANGUAGE_CHOICES: readonly DocLanguageOption[] = [
  { id: "ru", label: "русский", code: "ru" },
  { id: "az", label: "azərbaycan dili", code: "az" },
] as const;

/**
 * Языки, на которых семья готова учиться. Пусто = не сужаем.
 *
 * То же правило, что у форматов и стран: мягкий вариант побеждает. «Любой,
 * выучу» вместе с турецким — просьба не сужать при имеющейся склонности, и
 * сузить значило бы ответить не на её вопрос. «Только на том, который уже
 * знаю» тоже даёт пустой код: какой язык знаком, анкета не спрашивает, и
 * догадываться хуже, чем показать требования всех языков.
 */
export function studyLanguageCodes(values: unknown): string[] {
  if (!Array.isArray(values)) return [];
  const chosen = values.map((v) => optionOf(STUDY_LANGUAGES, v)).filter(Boolean);
  if (chosen.some((o) => o!.code === null)) return [];
  return chosen.map((o) => o!.code).filter((c): c is string => Boolean(c));
}

/**
 * Язык, который семья «уже знает», — язык её сертификата (К1, прогон 10.10).
 *
 * «Только на том, который уже знаю» — сужение, и какой язык знаком, анкета
 * всё-таки говорит: язык сертификата. Семья с IELTS, ответившая «только
 * знакомый», едет на английский, с HSK — на китайский.
 *
 * Это НЕ фильтр строк. Фильтр (studyLanguageCodes) снимает строки чужого
 * языка даже у вуза, где программ на языке семьи нет вовсе: семья с одним
 * HSK видела бы английские вузы без английского требования. Знакомый язык
 * выбирает путь варианта по языку программ (движок, languagePathsFor): есть у
 * вуза программы на нём — вариант считается по ним; нет — как при «любом».
 *
 * null — не выбрано «только знакомый», выбрано вместе с ним «любой, выучу»
 * (мягкий вариант побеждает), сертификата нет или он «другой».
 */
export function knownStudyLanguage(values: unknown, certificate: unknown): string | null {
  if (!Array.isArray(values)) return null;
  const chosen = values.map((v) => optionOf(STUDY_LANGUAGES, v)).filter((o): o is StudyLanguageOption => !!o);
  if (!chosen.some((o) => o.id === "known_only") || chosen.some((o) => o.id === "any")) return null;
  return optionOf(LANGUAGE_CERTIFICATES, certificate)?.language ?? null;
}

// ── вступительные экзамены ────────────────────────────────────────────
// examCode — код, которым экзамен зовётся в справочнике edu_exams.
//
// ЗДЕСЬ ЛЕЖАЛА ПОДПИСЬ, А НЕ КОД. У TR-YÖS значилось "TR-YÖS" — с дефисом и
// диакритикой, то есть ровно то, что показывают человеку. В справочнике код
// «tr_yos», и сравнение в верхнем регистре давало «TR_YOS» против «TR-YÖS»:
// семья, отметившая сданный TR-YÖS, читала «экзамен не сдавался».
//
// Побеждает справочник: идентификатор не содержит диакритики и дефисов. На
// них однажды спотыкается ещё что-нибудь — сортировка, URL, выгрузка, — а
// подпись рядом остаётся любой.
export type ExamOption = AnswerOption & {
  examCode: string | null;
  /**
   * Коды справочника, которые этот ответ ТОЖЕ закрывает. Нужны ровно там, где
   * один ответ анкеты означает несколько экзаменов справочника: итоговый
   * балл ДИМ — сумма двух этапов, и у ребёнка с итогом оба этапа сданы.
   */
  alsoCovers?: readonly string[];
};

export const ENTRANCE_EXAMS: readonly ExamOption[] = [
  { id: "sat", label: "SAT", examCode: "sat" },
  { id: "act", label: "ACT", examCode: "act" },
  { id: "tr_yos", label: "TR-YÖS", examCode: "tr_yos" },
  { id: "testas", label: "TestAS", examCode: "testas" },
  { id: "ucat", label: "UCAT", examCode: "ucat" },
  // CSCA — вступительный экзамен для иностранцев в Китай. Сдаётся по
  // предметам (китайский, математика, физика, химия, каждый из 100), и одного
  // общего балла у него нет — поэтому поля балла анкета для него НЕ заводит.
  // Отметка «сдавал» уже меняет ответ: «не сдавался» против «сдан, балл
  // уточнить».
  { id: "csca", label: "CSCA", examCode: "csca" },
  // ДИМ — домашний экзамен, и он же вход в 88 азербайджанских специальностей.
  // «ДИМ» закрывает и оба этапа (решение сессии данных 29.09): итоговый балл
  // — сумма двух этапов, у ребёнка с ним первый этап сдан (результат
  // действует 24 месяца), второй тоже. Без этого требование dim_stage1
  // (Эразмус, 0cde1a6f) печатало ребёнку с ДИМ «экзамен не сдавался».
  // Балла за отдельный этап анкета не спрашивает — такое требование с
  // порогом честно получит «сдан, балл не указан».
  {
    id: "dim",
    label: "ДИМ (централизованный экзамен)",
    examCode: "dim_total",
    alsoCovers: ["dim_stage1", "dim_stage2"],
  },
  // «Ничего не сдавал» и сданный SAT одновременно — противоречие.
  { id: "none", label: "нет, ничего", examCode: null, exclusive: true },
] as const;

/**
 * Коды справочника, которые семья закрыла своими ответами: основной код
 * варианта и всё, что он покрывает. Незнакомый ответ — как есть (старые
 * анкеты хранили код прямо). В верхнем регистре, как сравнивает движок.
 */
export function examCodesTaken(values: readonly string[]): Set<string> {
  const out = new Set<string>();
  for (const v of values) {
    const o = optionOf(ENTRANCE_EXAMS, v);
    if (!o) {
      out.add(v.toUpperCase());
      continue;
    }
    if (o.examCode) out.add(o.examCode.toUpperCase());
    for (const c of o.alsoCovers ?? []) out.add(c.toUpperCase());
  }
  return out;
}

// ── страны ────────────────────────────────────────────────────────────
// countryCode — код ISO, по которому вариант сопоставляется со справочником.
// Раньше сопоставление шло по name_ru: расчёт зависел не только от копирайтинга
// анкеты, но и от того, как страну назвали в другой таблице.
export type CountryOption = AnswerOption & { countryCode: string | null };

export const COUNTRY_CHOICES: readonly CountryOption[] = [
  { id: "tr", label: "Турция", countryCode: "TR" },
  { id: "de", label: "Германия", countryCode: "DE" },
  { id: "gb", label: "Британия", countryCode: "GB" },
  { id: "us", label: "США", countryCode: "US" },
  { id: "ae", label: "ОАЭ", countryCode: "AE" },
  { id: "other", label: "другие", countryCode: null },
  // «Покажите варианты» вместе с названной страной — просьба не сужать при
  // имеющейся склонности. Не противоречие: exclusive НЕ ставим.
  {
    id: "show_me",
    label: "пока не знаю, покажите варианты",
    countryCode: null,
  },
] as const;

/** «Покажите варианты» = не сужать выбор. Отдельная константа, не подстрока. */
export const COUNTRY_ANY_ID = "show_me";

/** Строка справочника, из которой строится вариант ответа. */
export type CountryRefRow = { code: string; name_ru: string; is_active: boolean };

/**
 * Пометка у страны, которую мы ещё не открыли.
 *
 * Стоит В ПОДПИСИ, а не отдельным полем: вариант рисуется одной строкой,
 * и второе поле пришлось бы протаскивать через три экрана ради одного слова.
 */
export const COUNTRY_CLOSED_MARK = " — пока не открыта";

/**
 * Список стран для анкеты — ИЗ СПРАВОЧНИКА, а не переписанный руками.
 *
 * ЗАЧЕМ. Список жил отдельно от is_active и разъехался: Голландию включили,
 * завели двенадцать вузов, сорок требований и все цены — а в анкете её нет,
 * и семья, назвавшая страны явно, не могла её выбрать никогда. Дойти до неё
 * получалось только через «покажите варианты».
 *
 * ВЫКЛЮЧЕННЫЕ ПОКАЗЫВАЮТСЯ ТОЖЕ, с пометкой. Убрать их значило бы перестать
 * собирать спрос — а спрос и есть то, по чему решают, какую страну открывать
 * следующей. Германию, Британию, США и ОАЭ семья по-прежнему называет, и
 * теперь ещё до заполнения видит, что мы их пока не открыли, — вместо
 * пустого отчёта в конце.
 *
 * ИДЕНТИФИКАТОР — строчный код страны. Совпадает с тем, что уже сохранено в
 * анкетах: tr, de, gb, us, ae. Переносить ответы не нужно.
 *
 * Пустой справочник — возвращаем статический список. Вопрос без стран хуже
 * любого рассинхрона: семья не сможет ответить вовсе.
 */
export function countryChoices(
  rows: readonly CountryRefRow[] | null | undefined,
): readonly CountryOption[] {
  const usable = (rows ?? []).filter((r) => r.code && r.name_ru);
  if (!usable.length) return COUNTRY_CHOICES;
  const built: CountryOption[] = usable
    .slice()
    .sort((x, y) => Number(y.is_active) - Number(x.is_active) || x.name_ru.localeCompare(y.name_ru, "ru"))
    .map((r) => ({
      id: r.code.toLowerCase(),
      label: r.is_active ? r.name_ru : r.name_ru + COUNTRY_CLOSED_MARK,
      countryCode: r.code.toUpperCase(),
    }));
  // СТРАНЫ, КОТОРЫЕ АНКЕТА ПРЕДЛАГАЛА, А СПРАВОЧНИК О НИХ НЕ ЗНАЕТ, ОСТАЮТСЯ.
  //
  // США есть в статическом списке и отсутствуют в edu_countries: перейдя на
  // справочник целиком, мы бы молча убрали страну из вопроса — и перестали
  // собирать спрос ровно там, где он собирался. Пометка у них та же: мы её не
  // открыли, а откуда мы это знаем, семье безразлично.
  const known = new Set(built.map((c) => c.countryCode));
  const legacy = COUNTRY_CHOICES.filter(
    (c) => c.countryCode !== null && !known.has(c.countryCode),
  ).map((c) => ({ ...c, label: c.label + COUNTRY_CLOSED_MARK }));

  // Хвост тот же и на своём месте: «другие» и «покажите варианты» — не страны
  // и в справочнике их нет.
  return [
    ...built.filter((c) => !c.label.endsWith(COUNTRY_CLOSED_MARK)),
    ...[...built.filter((c) => c.label.endsWith(COUNTRY_CLOSED_MARK)), ...legacy].sort((x, y) =>
      x.label.localeCompare(y.label, "ru"),
    ),
    ...COUNTRY_CHOICES.filter((c) => c.countryCode === null),
  ];
}

// ── что беспокоит в переезде ──────────────────────────────────────────
//
// Вариантами этого вопроса были голые подписи: значение ответа совпадало с
// текстом. Пока ответ никто не читал, это было допустимо по правилу выше. Но
// «ничего особенно» — взаимоисключающий вариант, и как только форма начинает
// его гасить, значение ИНТЕРПРЕТИРУЕТСЯ — значит обязано иметь id.
export const MAIN_WORRY_CHOICES: readonly AnswerOption[] = [
  { id: "language", label: "язык" },
  { id: "money", label: "деньги" },
  { id: "loneliness", label: "одиночество" },
  { id: "studies", label: "учёба" },
  { id: "daily_life", label: "еда и быт" },
  // «Ничего особенно» и «язык» одновременно — противоречие.
  { id: "nothing", label: "ничего особенно", exclusive: true },
  { id: "other", label: "другое" },
] as const;

/** Варианты-отрицания, гасящие остальные внутри своего вопроса. */
export function exclusiveIdsOf(options: readonly AnswerOption[]): string[] {
  return options.filter((o) => o.exclusive).map((o) => o.id);
}

/**
 * Что выбрано после нажатия на вариант — с учётом взаимоисключения.
 *
 * Форма и движок зовут одну и ту же функцию: правило, написанное дважды,
 * разойдётся.
 */
export function toggleWithExclusive(
  options: readonly AnswerOption[],
  selected: readonly string[],
  id: string,
): string[] {
  if (selected.includes(id)) return selected.filter((s) => s !== id);
  const opt = options.find((o) => o.id === id);
  if (opt?.exclusive) return [id]; // «нет ограничений» гасит всё остальное
  const exclusive = new Set(exclusiveIdsOf(options));
  return [...selected.filter((s) => !exclusive.has(s)), id];
}

/**
 * Разрешение противоречия в УЖЕ сохранённых данных.
 *
 * Форма противоречий больше не создаёт, но старые ответы и любой путь мимо
 * формы остаются. Правило: КОНКРЕТНОЕ ПОБЕЖДАЕТ. «Нет ограничений» вместе с
 * «халяль» — одно из двух ложно, и цена ошибок разная: выбросив «нет», мы
 * оставим ограничение, которого могло не быть; выбросив «халяль», отправим
 * ребёнка туда, где ему нечего есть.
 */
export function withoutExclusive(options: readonly AnswerOption[], selected: unknown): string[] {
  if (!Array.isArray(selected)) return [];
  const exclusive = new Set(exclusiveIdsOf(options));
  const ids = selected.filter((s): s is string => typeof s === "string");
  const concrete = ids.filter((s) => !exclusive.has(s));
  return concrete.length ? concrete : ids;
}

// ── «нет» в ограничениях ──────────────────────────────────────────────
// У здоровья, питания и жилья первый вариант всегда «нет», и он значит
// «ограничения отсутствуют». Один id на три вопроса.
export const LIMIT_NONE_ID = "none";

export const HEALTH_CHOICES: readonly AnswerOption[] = [
  { id: LIMIT_NONE_ID, label: "нет" },
  { id: "yes", label: "да" },
] as const;

export const HOUSING_CHOICES: readonly AnswerOption[] = [
  { id: LIMIT_NONE_ID, label: "нет" },
  { id: "yes", label: "да" },
] as const;

export const DIET_CHOICES: readonly AnswerOption[] = [
  // «Нет» и «халяль» одновременно — противоречие, а не оттенок.
  { id: LIMIT_NONE_ID, label: "нет", exclusive: true },
  { id: "halal", label: "халяль" },
  { id: "vegetarian", label: "вегетарианство" },
  { id: "allergies", label: "аллергии" },
  { id: "other", label: "другое" },
] as const;

// ── чтение сохранённых ответов ────────────────────────────────────────

/**
 * Привести сохранённое значение к идентификатору.
 *
 * Значение — это id, и ничего кроме. Подпись варианта сюда не попадает: анкета
 * сохраняет id, а экран показывает label. Незнакомое значение возвращается как
 * null, а не угадывается — иначе опечатка в данных тихо станет валидным
 * ответом.
 */
export function toId<T extends AnswerOption>(options: readonly T[], value: unknown): string | null {
  if (typeof value !== "string" || value === "") return null;
  return options.some((o) => o.id === value) ? value : null;
}

/** Найти вариант по сохранённому значению. Значение — это id. */
export function optionOf<T extends AnswerOption>(options: readonly T[], value: unknown): T | null {
  const id = toId(options, value);
  return id ? (options.find((o) => o.id === id) as T) : null;
}

/** Подпись для показа. Незнакомое значение отдаём как есть, а не прячем. */
export function labelOf<T extends AnswerOption>(options: readonly T[], value: unknown): string | null {
  const o = optionOf(options, value);
  if (o) return o.label;
  return typeof value === "string" && value ? value : null;
}

export function labelsOf<T extends AnswerOption>(options: readonly T[], values: unknown): string[] {
  if (!Array.isArray(values)) return [];
  return values.map((v) => labelOf(options, v)).filter((v): v is string => Boolean(v));
}

/** Верхняя граница бюджета в долларах. null = «не знаю», а не ноль. */
export function budgetCeilingUsd(value: unknown): number | null {
  return optionOf(BUDGET_RANGES, value)?.ceilingUsd ?? null;
}

// ── группы ДИМ ────────────────────────────────────────────────────────
//
// ЗАЧЕМ ВООБЩЕ СПРАШИВАТЬ ГРУППУ. Балл ДИМ осмыслен только вместе со шкалой:
// 614,6 из 700 у медицины и 60,0 из 100 у композиции — не «в десять раз
// доступнее», а разные системы координат. Пока группа неизвестна, движок по
// правилу из 20260910120000 честно отвечает «сравнить нельзя» — и отвечал так
// по ВСЕМ 88 азербайджанским специальностям сразу.
//
// СПРАШИВАЕМ ГРУППУ, А НЕ ШКАЛУ. Группу семья знает — это слово из её мира.
// «dim_group4_700» не знает никто, кроме нашего справочника, и просить семью
// перевести за нас нельзя. Соответствие живёт здесь, рядом с вариантом, — тем
// же приёмом, что examCode у экзаменов и countryCode у стран.
//
// ШКАЛА ОДНА НА ГРУППЫ I–IV, И ЭТО НЕ ДОГАДКА.
//
// Раньше здесь стоял null у групп I, II и III: считалось, что шкала заведена
// только для четвёртой. Это читалось по ИМЕНИ строки — dim_group4_700, — а не
// по её содержанию. Примечание самой шкалы в справочнике говорит прямо:
// «Шкала приёма для групп I–IV: блок 400 плюс выпускной 300. По ней
// публикуются проходные баллы вузов и по ней же читают балл турецкие вузы».
// Максимум 700 складывается одинаково у всех четырёх: 300 за выпускной этап
// плюс 400 за блок профильных предметов.
//
// ЧТО ЭТО СТОИЛО. Пустая шкала для движка значит «сравнить нельзя», и
// абитуриент групп I–III получал scale_unknown вместо сравнения с живыми
// порогами: METU Северный Кипр 450, Сабанджи 490, Билькент 550. Три
// требования, и мимо них проходили три четверти сдающих ДИМ.
//
// Имя строки остаётся dim_group4_700 — это данные, и переименование их
// касается. Здесь важно, что означает строка, а не как она названа.
//
// Группа V не присоединяется намеренно: её экзамен устроен иначе, максимум
// 100, и балл по нему НЕСРАВНИМ с группами I–IV — об этом сказано в
// примечании второй шкалы теми же словами.
export type DimGroupOption = AnswerOption & { scaleCode: string | null };

export const DIM_GROUPS: readonly DimGroupOption[] = [
  { id: "dim_group_1", label: "I группа", scaleCode: "dim_group4_700" },
  { id: "dim_group_2", label: "II группа", scaleCode: "dim_group4_700" },
  { id: "dim_group_3", label: "III группа", scaleCode: "dim_group4_700" },
  { id: "dim_group_4", label: "IV группа", scaleCode: "dim_group4_700" },
  { id: "dim_group_5", label: "V группа (творческая)", scaleCode: "dim_group5_100" },
  // «Не помню» — честный ответ, и он лучше выдуманной группы: движок скажет
  // «сравнить нельзя», а не сравнит балл не с той шкалой.
  { id: "dim_group_unknown", label: "не помню", scaleCode: null },
] as const;

/** Шкала балла по названной группе. NULL — шкала неизвестна, сравнивать нечем. */
export function dimScaleCode(group: string | null | undefined): string | null {
  if (!group) return null;
  return DIM_GROUPS.find((g) => g.id === group)?.scaleCode ?? null;
}

/**
 * Группа ДИМ из ответа: только известный идентификатор варианта. Всё прочее —
 * NULL, а не «как-нибудь похоже»: по группе выбирается шкала балла.
 */
export function dimGroupOf(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  return DIM_GROUPS.some((g) => g.id === raw) ? raw : null;
}

/** Потолок шкалы: V группа — 100, группы I–IV и «группа не названа» — 700. */
const DIM_SCALE_MAX: Record<string, number> = { dim_group4_700: 700, dim_group5_100: 100 };
const DIM_MAX_ANY = 700;

/**
 * Балл ДИМ из свободной строки анкеты.
 *
 * Поле анкеты — текст, и туда пишут всё: «614,6», «около 500», «не знаю».
 * Движку уходит только число в пределах шкалы названной группы (0–700 для
 * I–IV и для неназванной, 0–100 для V). Остальное — NULL, то есть «балл не
 * указан»: так движок уже отвечает тому, кто отметил ДИМ без балла, — «сдан,
 * балл не указан, уточнить», а не выдуманное «не хватает».
 *
 * Возвращается строка как её ввели (без пробелов по краям): запятая в
 * «614,6» — раскладка семьи, движок её читает, а печать остаётся её словами.
 */
export function dimScoreOf(raw: unknown, group: string | null | undefined): string | null {
  if (typeof raw !== "string" && typeof raw !== "number") return null;
  const s = String(raw).trim();
  const t = s.replace(",", ".");
  if (!/^\d+(\.\d+)?$/.test(t)) return null;
  const n = Number(t);
  const scale = dimScaleCode(group);
  const max = scale ? (DIM_SCALE_MAX[scale] ?? DIM_MAX_ANY) : DIM_MAX_ANY;
  if (!Number.isFinite(n) || n < 0 || n > max) return null;
  return s;
}

// ── система школы ─────────────────────────────────────────────────────
//
// ЗАЧЕМ. Ребёнок из бакинской школы с A-level или IB попадает в британский
// вуз СРАЗУ НА ПЕРВЫЙ КУРС, а ребёнок с аттестатом теряет год на foundation.
// До этого вопроса движок считал вторыми всех: спросить было нечем.
//
// Манчестер пишет прямо: «If you are studying A-levels or the International
// Baccalaureate (IB) then you can apply for direct entry to the first year».
//
// qualification — значение справочника (edu_requirements.qualification), и
// сопоставление живёт здесь, рядом с вариантом ответа, как examCode у
// экзаменов и scaleCode у групп ДИМ. В базе профиля хранится ИДЕНТИФИКАТОР
// варианта, а не это значение: переименуем вариант — сломается одно место.
export type SchoolSystemOption = AnswerOption & { qualification: string | null };

export const SCHOOL_SYSTEMS: readonly SchoolSystemOption[] = [
  { id: "school_national", label: "обычная школа, аттестат", qualification: "national_certificate" },
  { id: "school_a_level", label: "A-level", qualification: "a_level" },
  { id: "school_ib", label: "International Baccalaureate (IB)", qualification: "ib" },
  {
    // IGCSE без A-level — отдельный ответ, а не «почти A-level»: британские
    // вузы прямого входа по нему не дают, и склеить их значило бы обещать
    // первый курс тому, кому его не дадут.
    id: "school_igcse",
    label: "IGCSE, без A-level",
    qualification: "igcse",
  },
  {
    // «Другая» и «не знаю» ведут себя одинаково и намеренно: квалификация
    // остаётся неизвестной, и движок НЕ отбирает строки — ребёнок видит все,
    // как видел до появления вопроса. Догадка здесь стоила бы года.
    id: "school_other",
    label: "другая система",
    qualification: null,
  },
] as const;

/** Квалификация по ответу семьи. NULL — не назвали, отбирать строки нечем. */
export function qualificationOf(system: string | null | undefined): string | null {
  if (!system) return null;
  return SCHOOL_SYSTEMS.find((s) => s.id === system)?.qualification ?? null;
}

/**
 * Месяц рождения в той единственной форме, которую мы храним: «2009-11».
 *
 * Одна проверка на запись в колонку и на чтение в профиль семьи — чтобы
 * «сохранили» и «прочитали» не разошлись. Дня нет намеренно: он не нужен,
 * а выдуманный день читался бы как день рождения. Всё, что не подходит, —
 * null, то есть «возраст неизвестен», а не «совершеннолетний».
 */
export function birthMonthOf(v: unknown): string | null {
  return typeof v === "string" && /^\d{4}-(0[1-9]|1[0-2])$/.test(v) ? v : null;
}
